import React from 'react';
import NavBar from './navbar/NavBar';
import { initializeIcons, Spinner, SpinnerSize } from '@fluentui/react';
import './App.css';
import Routes from './App/Components/Routes';
import { ServiceFactoryClientExecuteApi } from './Common/Services/ServiceFactoryToExecuteApi';
import { UATConstants, HttpVerbs, Settings } from './Common/Models/UATConstants';
import ErrorResponse from './Models/ErrorResponse';
import { TenantAuthorization, UserContext } from './Models/UserProfile';
import AuthProvider from './AuthProvider';
import { connect } from 'react-redux';
import { setUserProfile } from './reducers/userProfileReducer';
import { setNotifications } from './actions/Notifications';
import { AppFrameProvider } from '@coherence-design-system/app-frame';
import { NotificationProvider, NotificationBubbles } from './notifications';
import { LeftNavigation } from './left-navigation/LeftNavigation';
import { setVacTenant } from './reducers/vacTenantReducer';
import i18n from './Locales/i18n';
import { mergeTenantConfig, setTenantConfig } from './tenantConfig/slice/tenantSlice';
import { setCompetitorsTaxonomy } from './tenantConfig/slice/taxonomySlice';
import { removeDisabledElementsFromTabIndex } from './utils/accessibility';
import { MapIFieldDefinitionDTOToIFieldDefinition } from './Common/manageViews/utils/helpers/MapIFieldDefinitionDTOToIFieldDefinition';
import { DIRECT_LANDING_CLOSED_EVENT, isDirectLandCreate, isHeaderless } from './dashboard/DirectLanding/urlparams';
import { getTransformedUserContext } from './Common/Utilities/Utilities';
import { fetchWithIndexedDB, setCache } from './Common/Services/IndexedDBService';
import { store } from './Store';
import { LocalTenantConfig } from './tenantConfig';
import { FluentProvider, webLightTheme, IdPrefixProvider } from '@fluentui/react-components';
import { store as rtkStore } from './Common/RTK/store';

export interface AppState {
  user?: UserContext;
  apiResponseState?: any;
  profilePicState?: any;
  account?: any;
  apiToken?: string;
  coreApiToken?: string;
  showSpinner: boolean;
  directLandingSpinner: boolean;
  isDirectLandingCreate: boolean;
  directLandingMessage: string;
  togleleftNav: boolean;
  showManageViews: boolean;
  isNavCollapsed: boolean;
  isProfilePicLoading: boolean;
  vacTenant?: TenantAuthorization[];
}

class App extends React.PureComponent<any, AppState> {
  constructor(props: any) {
    super(props);
    this.state = {
      user: undefined,
      profilePicState: '',
      account: undefined,
      apiToken: '',
      showSpinner: true,
      directLandingSpinner: isDirectLandCreate(),
      isDirectLandingCreate: isDirectLandCreate(),
      directLandingMessage: UATConstants.PermissionMsgs.redirectMessag,
      togleleftNav: false,
      showManageViews: false,
      isNavCollapsed: false,
      isProfilePicLoading: false,
      vacTenant: undefined,
    };
    this.handleManageViewClick = this.handleManageViewClick.bind(this);
    this.toggleNavCollapsed = this.toggleNavCollapsed.bind(this);
    this.handleRouteManageViewClick = this.handleRouteManageViewClick.bind(this);
  }
  private observer: MutationObserver | null = null;

  private profilePicTimeout: ReturnType<typeof setTimeout> | null = null;

  componentDidMount() {
    window.addEventListener(DIRECT_LANDING_CLOSED_EVENT, this.handleDirectLandingClosed);
    initializeIcons();
    // Defer non-critical fabric CSS to avoid render-blocking (310KB)
    import('office-ui-fabric-core/dist/css/fabric.min.css');
    this.props.onSignIn();

    // Fix for disabled elements should not be focusable on tab key press.
    removeDisabledElementsFromTabIndex();

    this.observer = new MutationObserver((_, _observer) => {
      removeDisabledElementsFromTabIndex();
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  componentWillUnmount() {
    window.removeEventListener(DIRECT_LANDING_CLOSED_EVENT, this.handleDirectLandingClosed);
    // Disconnect the observer to avoid memory leaks.
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Clear profile pic timeout
    this.clearProfilePicTimeout();
  }

  private clearProfilePicTimeout = () => {
    if (this.profilePicTimeout) {
      clearTimeout(this.profilePicTimeout);
      this.profilePicTimeout = null;
    }
  };

  private updateDefaultTenant = async (defaultTenant: string | null): Promise<void> => {
    if (!this.state.user) {
      throw new Error('User context is not available');
    }

    const updatedUserContext: UserContext = {
      ...this.state.user,
      userSettings: {
        ...this.state.user.userSettings,
        defaultTenant,
      },
    };

    this.props.setUserProfile(updatedUserContext);
    await new Promise<void>((resolve) =>
      this.setState({ user: updatedUserContext }, resolve)
    );
    await setCache(UATConstants.IndexedDBKeyNames.UserContext, updatedUserContext);
  };


  handleHamburgerClick = (data: boolean) => {
    this.setState({ togleleftNav: data });
  };
  private handleDirectLandingClosed = () => {
    this.setState({
      isDirectLandingCreate: isDirectLandCreate(),
      isNavCollapsed: false,
    });
  };
  handleManageViewClick = (data: boolean) => {
    this.setState({ showManageViews: data });
  };
  handleRouteManageViewClick = (data: boolean) => {
    this.setState({ showManageViews: data });
  };
  toggleNavCollapsed = () => {
    this.setState({ isNavCollapsed: !this.state.isNavCollapsed });
    this.getPinnedState();
    window.dispatchEvent(new Event('storage'));
  };
  async callVacTenantAPI(props: any) {
    const hasApiTokenChanged = props.apiAccessToken !== this.props.apiAccessToken;
    const hasCoreApiTokenChanged = props.coreApiAccessToken !== this.props.coreApiAccessToken;
    const tokensAreValid = props.apiAccessToken !== null && props.coreApiAccessToken !== null;

    if (hasApiTokenChanged || hasCoreApiTokenChanged || tokensAreValid) {
      // Set token in the component state and in session storage
      this.setState({ apiToken: props.apiAccessToken });
      sessionStorage.setItem('jwtTokenForApi', props.apiAccessToken);
      sessionStorage.setItem('jwtTokenForGraph', props.graphAccessToken);
      sessionStorage.setItem('jwtTokenForCoreApi', props.coreApiAccessToken);
      window.dispatchEvent(new Event('storage'));

      try {
        const tenantList = await fetchWithIndexedDB(
          UATConstants.IndexedDBKeyNames.Tenants, // Cache key for tenant data
          async () => {
            const url = `${UATConstants.CommonServicesWebApiUrl}/api/v1/raid/transactional/tenants`;
            return new Promise<any[]>((resolve, reject) => {
              ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
                url,
                [],
                HttpVerbs.get,
                [],
                (response: UserContext) => {
                  if (response && response.value) {
                    const tenants = response.value.map((tenant: any) => ({
                      ...tenant,
                      FieldConfigurations: MapIFieldDefinitionDTOToIFieldDefinition(
                        tenant?.FieldConfigurations || []
                      ),
                    }));
                    resolve(tenants);
                  } else {
                    reject(new Error('Failed to fetch tenant data'));
                  }
                },
                (error: ErrorResponse) => {
                  console.error('Error fetching tenant data:', error);
                  reject(error);
                },
                false
              );
            });
          },
          Settings.IDB_CACHE_DURATION
        );
        this.setState({
          vacTenant: tenantList,
        });
        const transformedTenantList = tenantList.map((tenant: any) => {
          if (tenant.Tenant === 'Microsoft Federal') {
            return {
              ...tenant,
              Default: false,
            };
          }
          return tenant;
        });

        // Microsoft Marketplace is no longer flight-gated: always ensure the tenant is present in
        // the tenant list so `getTenantId()` resolves it, `mergeTenantConfig` pulls in its
        // `LocalTenantConfig` FeatureConfig, and the left nav renders its link. If the backend
        // tenants API doesn't return it, inject it client-side; otherwise keep the backend entry.
        const isMarketplaceTenant = (t: any) => t?.TenantID === UATConstants.TenantIds.MARKETPLACE;
        const finalTenantList = transformedTenantList.some(isMarketplaceTenant)
          ? transformedTenantList
          : [
            ...transformedTenantList,
            {
              TenantID: UATConstants.TenantIds.MARKETPLACE,
              Tenant: 'Microsoft Marketplace',
              ShortName: 'Marketplace',
              Default: true,
              Enabled: true,
              ArtemisCall: true,
              FieldConfigurations: [],
            },
          ];

        this.props.setVacTenant(finalTenantList);

        // Also populate the RTK store with tenant config (keyed by TenantID)
        const tenantConfigObject = finalTenantList.reduce((acc: any, tenant: any) => {
          if (tenant.TenantID) {
            acc[tenant.TenantID] = tenant;
          }
          return acc;
        }, {});
        rtkStore.dispatch(setTenantConfig(tenantConfigObject));
      } catch (error) {
        console.error('Error handling tenant data:', error);
      }
    }
  }
  async callTaxonomyAPI() {
    try {
      const taxonomyData = await fetchWithIndexedDB(
        UATConstants.IndexedDBKeyNames.Taxonomy,
        async () => {
          const url = `${UATConstants.CommonServicesWebApiUrl}/api/v1/Taxonomy/GetAllActiveTaxonomy?typeCode=COMPETITORS&period=FY27`;
          return new Promise<any[]>((resolve, reject) => {
            ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
              url,
              [],
              HttpVerbs.get,
              [],
              (response: any) => {
                if (response) {
                  let items: any[] = [];
                  if (Array.isArray(response)) {
                    items = response;
                  } else if (response.value && Array.isArray(response.value)) {
                    items = response.value;
                  } else if (response.data && Array.isArray(response.data)) {
                    items = response.data;
                  } else if (response.result && Array.isArray(response.result)) {
                    items = response.result;
                  } else if (typeof response === 'object') {
                    // If the response is an object with taxonomy items as properties
                    items = Object.values(response).filter(v => Array.isArray(v)).flat();
                    if (items.length === 0) {
                      items = [response];
                    }
                  }
                  resolve(items);
                } else {
                  reject(new Error('Failed to fetch taxonomy data'));
                }
              },
              (error: ErrorResponse) => {
                console.error('Error fetching taxonomy data:', error);
                reject(error);
              },
              false
            );
          });
        },
        Settings.IDB_CACHE_DURATION
      );
      rtkStore.dispatch(setCompetitorsTaxonomy(taxonomyData));
    } catch (error) {
      console.error('Error handling taxonomy data:', error);
    }
  }
  async UNSAFE_componentWillReceiveProps(props: any) {
    if (this.props.account !== props.account) {
      this.setState({ account: props.account });
    }

    if (this.props.graphAccessToken !== props.graphAccessToken) {
      this.props.getCoreAPIAccessToken();
      this.props.getAPIAccessToken();
    }

    if (
      (props.apiAccessToken !== this.props.apiAccessToken ||
        props.coreApiAccessToken !== this.props.coreApiAccessToken) &&
      props.apiAccessToken !== null &&
      props.coreApiAccessToken !== null
    ) {
      this.setState({ apiToken: props.apiAccessToken });
      sessionStorage.setItem('jwtTokenForApi', props.apiAccessToken);
      sessionStorage.setItem('jwtTokenForGraph', props.graphAccessToken);
      sessionStorage.setItem('jwtTokenForCoreApi', props.coreApiAccessToken);
      window.dispatchEvent(new Event('storage'));

      try {
        const userContext = await fetchWithIndexedDB(
          UATConstants.IndexedDBKeyNames.UserContext, // Cache key
          () =>
            new Promise((resolve, reject) => {
              let url = `${UATConstants.ApiPaths.UserContext}`;
              if (
                window.location.pathname !== UATConstants.Routes.LandingPage &&
                window.location.pathname !== UATConstants.Routes.Dashboard
              ) {
                url = `${UATConstants.ApiPaths.UserContext}${UATConstants.UATKeyTerms.landingPageFlag}`;
              }

              ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
                url,
                [],
                HttpVerbs.get,
                [],
                (response: UserContext) => {
                  if (response) {
                    resolve(response);
                  } else {
                    reject(new Error('Failed to fetch user context'));
                  }
                },
                (error: ErrorResponse) => {
                  console.error('Error fetching user context:', error);
                  reject(error);
                },
                false,
                undefined,
                undefined
              );
            }),
          Settings.IDB_CACHE_DURATION // Cache duration: 1 hour
        );

        // Update state and Redux store with the user context
        sessionStorage.setItem('jwtUserAccountName', userContext.displayName);
        sessionStorage.setItem('jwtUserAccountEmail', userContext.email);

        const transformedUserContext = userContext;

        this.props.setUserProfile(transformedUserContext);
        this.setState({
          user: transformedUserContext,
          showSpinner: false,
        });

        const userLanguage = transformedUserContext.userSettings?.LanguageCode;
        if (userLanguage) {
          i18n.changeLanguage(userLanguage);
        } else {
          i18n.changeLanguage(i18n.language);
        }

        await this.callVacTenantAPI(props);
        await this.callTaxonomyAPI();
      } catch (error) {
        console.error('Error handling user context:', error);
      }
    }

    // Handle profile picture loading
    if (!this.props.profilePic && !this.state.isProfilePicLoading) {
      this.setState({ isProfilePicLoading: true });
      this.props.onRequestProfilePic();

      // Set a timeout to reset loading flag after 30 seconds
      this.profilePicTimeout = setTimeout(() => {
        if (this.state.isProfilePicLoading) {
          console.warn('Profile picture request timeout, resetting loading flag');
          this.setState({ isProfilePicLoading: false });
        }
      }, 30000);
    }

    // Reset loading flag if there's an error or if profile pic request failed
    if (this.props.error && this.state.isProfilePicLoading) {
      this.setState({ isProfilePicLoading: false });
      this.clearProfilePicTimeout();
    }

    // Call callVacTenantAPI immediately after props are received and processed
    // await this.callVacTenantAPI(props);

    if (this.props.apiProfileResponse !== props.apiProfileResponse) {
      this.setState({ user: props.apiProfileResponse });
    }
    if (this.props.profilePic !== props.profilePic) {
      const profilePicReader = props.profilePic;
      if (profilePicReader) {
        const reader = new FileReader();
        reader.readAsDataURL(profilePicReader);
        reader.onloadend = () => {
          const profileBase64 = reader.result;
          this.setState({
            profilePicState: profileBase64,
            isProfilePicLoading: false,
          });
          this.clearProfilePicTimeout();
        };
      } else {
        // Reset loading flag if profilePic becomes null/undefined
        this.setState({ isProfilePicLoading: false });
        this.clearProfilePicTimeout();
      }
    }
  }
  showTeachingBubble = () => {
    return (
      !this.state.showSpinner &&
      (window.location.pathname === UATConstants.Routes.DashboardSummary ||
        window.location.pathname === UATConstants.Routes.Dashboard)
    );
  };
  getPinnedState = () => {
    const dynamicCopilotPinnedState = sessionStorage.getItem('copilotPinnedState');
    if (dynamicCopilotPinnedState === 'false') {
      return true;
    }
    if (dynamicCopilotPinnedState) {
      try {
        return JSON.parse(dynamicCopilotPinnedState);
      } catch (e) {
        console.error('Error parsing copilotPinnedState:', e);
        return null;
      }
    }
    return null;
  };

  stopLoadingDirectLandingSpinner = () => {
    this.setState({ directLandingSpinner: false });
  };

  updateDirectLandingMessage = () => {
    this.setState({ directLandingMessage: UATConstants.PermissionMsgs.redirectNotAuthMessage });
  };

  render() {
    return (
      <>
        <a
          id='directLandingLoader'
          style={{ display: 'none' }}
          onClick={this.stopLoadingDirectLandingSpinner}></a>
        <a
          id='directLandingLoaderMessageUpdate'
          style={{ display: 'none' }}
          onClick={this.updateDirectLandingMessage}></a>
        <IdPrefixProvider value="uat">

          <FluentProvider theme={webLightTheme}>
            <NotificationProvider>
              <AppFrameProvider defaultThemeKey='light'>
                {!isHeaderless() && (
                <div role="banner" slot='header' style={{ backgroundColor: '#0078d4' }}>
                  {this.props.apiAccessToken && (
                    <NavBar
                      user={this.state.user}
                      profileBase64={this.state.profilePicState}
                      account={this.props.account}
                      isNavCollapsed={this.state.isNavCollapsed}
                      toggleNavCollapsed={this.toggleNavCollapsed}
                      onHamburgerClick={this.handleHamburgerClick}
                    />
                  )}
                </div>
                )}
                {(window.location.pathname === UATConstants.Routes.LandingPage ||
                  window.location.pathname === UATConstants.Routes.Dashboard ||
                  window.location.pathname.toLowerCase().replace(/\s/g, '') ===
                  UATConstants.Routes.Home ||
                  window.location.pathname === UATConstants.Routes.Overview ||
                  window.location.pathname === UATConstants.Routes.DashboardSummary ||
                  (window.location.pathname === UATConstants.Routes.TenantOverviewHub && !this.state.isDirectLandingCreate) ||
                  window.location.pathname === UATConstants.Routes.ManageView) && (
                    <div slot='navigation' id='leftNavigation'>
                      {this.state.user && (
                        <LeftNavigation
                          user={this.state.user}
                          updateDefaultTenant={this.updateDefaultTenant}
                          isManageViewsOpen={this.state.showManageViews}
                          handleManageViewClick={this.handleManageViewClick}
                          isNavCollapsed={this.state.isNavCollapsed}></LeftNavigation>
                      )}
                    </div>
                  )}
                <div
                  slot='main'
                  role='main'
                  id='main'
                  style={{ overflowY: 'hidden', flexDirection: 'column', position: 'relative' }}>
                  <div style={{ overflowY: 'auto', flex: 'auto', display: 'flex' }}>
                    {
                      // Instead of 'home' send current page flag
                      this.showTeachingBubble() && <NotificationBubbles page='home' />
                    }
                    {this.state.user && (
                      <Routes
                        user={this.state.user}
                        vacTenant={this.state.vacTenant}
                        userAuthToken={this.state.apiToken}
                        previousPaths={[]}
                        searchKeyword=''
                        post_logout_redirect_uri=''
                        showManageViews={this.state.showManageViews}
                        handleManageViewClick={this.handleRouteManageViewClick}
                        isNavCollapsed={this.state.isNavCollapsed}
                        toggleNavCollapsed={this.toggleNavCollapsed}
                      />
                    )}
                    {this.state.showSpinner && (
                      <Spinner
                        size={SpinnerSize.large}
                        label={UATConstants.PlaceholdersAndTitles.Loading}
                        ariaLive='assertive'
                        className='loading-panel-full'
                      />
                    )}
                    {!this.state.showSpinner && this.state.directLandingSpinner && (
                      <Spinner
                        size={SpinnerSize.large}
                        label={this.state.directLandingMessage}
                        ariaLive='assertive'
                        className='loading-panel-full'
                      />
                    )}
                  </div>
                </div>
              </AppFrameProvider>
            </NotificationProvider>
          </FluentProvider>
        </IdPrefixProvider>
      </>
    );
  }
}

const mapDispatchToProps = {
  setUserProfile: setUserProfile,
  setNotifications: setNotifications,
  setVacTenant: setVacTenant,
  mergeTenantConfig: mergeTenantConfig,
};
const mapStateToProps = (storeObj: any) => ({
  dashboardSplitMode: storeObj.dashboardReducer.dashboardSplitMode,
});

export default AuthProvider(connect(mapStateToProps, mapDispatchToProps)(App));
// Generated by GitHub Copilot | Updated on: 2026-04-13 | 35 lines modified | Added taxonomy API call for competitors data on page load

// Generated by GitHub Copilot | Updated on: 2026-04-13 | 1 lines modified | Hide left nav for direct landing create URLs
// Generated by GitHub Copilot | Updated on: 2026-05-15 | 2 lines modified | Deferred office-ui-fabric-core CSS load to eliminate 310KB render-blocking request
// Generated by GitHub Copilot | Updated on: 2026-07-22 | Replaced serverDrivenForms preview-flag gating with MarketplaceTenant flight gating for the Marketplace tenant (inject when flighted + missing, strip when un-flighted + present); removed the now-dead serverDrivenForms re-run subscription and mapStateToProps entry
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 30 lines added | Inject Microsoft Marketplace tenant into the tenant list behind the serverDrivenForms preview flag (so getTenantId resolves it + left nav shows it)
// Generated by GitHub Copilot | Updated on: 2026-07-21 | 8 lines added | Subscribe to serverDrivenForms from the Redux store and re-run callVacTenantAPI on flip (replaces NavBar page reload)

// Generated by GitHub Copilot | Updated on: 2026-08-18 | 20 lines modified | Removed MarketplaceAccess flight gating for Marketplace tenant; always inject if missing; dropped unused imports
// Generated by GitHub Copilot | Updated on: 2026-10-05 | 45 lines modified | Added uncached user context refresh
// Generated by GitHub Copilot | Updated on: 2026-10-06 | 40 lines modified | Optimistically cached default tenant
